import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ShieldCheck, ShieldWarning, WarningCircle, CheckCircle, Info,
  ArrowsClockwise, CaretDown, CaretUp, Copy, Check,
} from "@phosphor-icons/react";

const STATUS_META = {
  pass: {
    icon: CheckCircle,
    color: "text-emerald-400",
    bg: "bg-emerald-500/10",
    border: "border-emerald-500/30",
    dot: "bg-emerald-400",
    label: "OK",
  },
  warn: {
    icon: WarningCircle,
    color: "text-amber-400",
    bg: "bg-amber-500/10",
    border: "border-amber-500/30",
    dot: "bg-amber-400",
    label: "Warning",
  },
  fail: {
    icon: ShieldWarning,
    color: "text-red-400",
    bg: "bg-red-500/10",
    border: "border-red-500/30",
    dot: "bg-red-400",
    label: "Fail",
  },
  info: {
    icon: Info,
    color: "text-slate-300",
    bg: "bg-slate-500/10",
    border: "border-slate-500/30",
    dot: "bg-slate-400",
    label: "Info",
  },
};

const OVERALL_META = {
  pass: { text: "Strong", color: "text-emerald-400" },
  warn: { text: "Needs attention", color: "text-amber-400" },
  fail: { text: "Action required", color: "text-red-400" },
};

const STATUS_MARK = { pass: "✅", warn: "⚠️", fail: "🔴", info: "ℹ️" };

function buildMarkdown(data) {
  const { overall, counts, checks, generated_at } = data;
  const overallText = (OVERALL_META[overall] || {}).text || overall;
  const when = new Date(generated_at || Date.now());
  const lines = [
    "# SentryWatch — Security posture snapshot",
    "",
    `- **Overall status**: ${STATUS_MARK[overall] || ""} ${overallText}`,
    `- **Controls passing**: ${counts.pass}/${counts.total}` +
      (counts.fail ? ` · ${counts.fail} fail` : "") +
      (counts.warn ? ` · ${counts.warn} warn` : ""),
    `- **Generated at**: ${when.toISOString()}`,
    "",
    "| Control | Status | Detail |",
    "| --- | --- | --- |",
    ...checks.map((c) => {
      const mark = STATUS_MARK[c.status] || "";
      const status = (c.status || "").toUpperCase();
      // pipe-escape any pipes inside the message
      const msg = (c.message || "").replace(/\|/g, "\\|");
      return `| ${c.label} | ${mark} ${status} | ${msg} |`;
    }),
    "",
    "_Generated by SentryWatch — https://watch.sentryit.com_",
  ];
  return lines.join("\n");
}

export default function SecurityPostureCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const copySnapshot = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(buildMarkdown(data));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      console.warn("clipboard write failed:", e);
    }
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/security/posture");
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <div className="sw-card p-4" data-testid="security-posture-card">
        <div className="text-xs text-slate-500">Loading security posture…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="sw-card p-4" data-testid="security-posture-card">
        <div className="text-xs text-red-400">Failed to load: {error}</div>
      </div>
    );
  }
  if (!data) return null;

  const overall = OVERALL_META[data.overall] || OVERALL_META.warn;
  const { pass, warn, fail, total } = data.counts;

  return (
    <div className="sw-card overflow-hidden" data-testid="security-posture-card">
      <div className="px-5 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-md border flex items-center justify-center shrink-0 ${
            data.overall === "pass"
              ? "border-emerald-500/40 bg-emerald-500/10"
              : data.overall === "fail"
                ? "border-red-500/40 bg-red-500/10"
                : "border-amber-500/40 bg-amber-500/10"
          }`}>
            <ShieldCheck size={20} weight="duotone" className={overall.color} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">
              Security posture
            </div>
            <div className="flex items-baseline gap-2">
              <span className={`font-display text-lg font-bold ${overall.color}`}
                data-testid="security-posture-overall">
                {overall.text}
              </span>
              <span className="text-xs text-slate-500">
                {pass}/{total} controls passing
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden sm:flex items-center gap-2 text-xs">
            {fail > 0 && (
              <span className="inline-flex items-center gap-1 text-red-400" data-testid="posture-fail-count">
                <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> {fail} fail
              </span>
            )}
            {warn > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-400" data-testid="posture-warn-count">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {warn} warn
              </span>
            )}
            {fail === 0 && warn === 0 && (
              <span className="inline-flex items-center gap-1 text-emerald-400">
                <CheckCircle size={12} weight="fill" /> All clear
              </span>
            )}
          </div>
          <button onClick={load}
            data-testid="security-posture-refresh"
            title="Refresh"
            className="p-1.5 text-slate-400 hover:text-brand-cyan transition-colors">
            <ArrowsClockwise size={13} weight="bold" />
          </button>
          <button
            onClick={copySnapshot}
            data-testid="security-posture-copy"
            title="Copy posture as Markdown"
            className={`text-[11px] font-semibold uppercase tracking-[0.15em] inline-flex items-center gap-1 transition-colors ${
              copied ? "text-emerald-400" : "text-slate-400 hover:text-brand-cyan"
            }`}>
            {copied
              ? <><Check size={11} weight="bold" /> Copied</>
              : <><Copy size={11} weight="bold" /> Copy</>}
          </button>
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="security-posture-toggle"
            className="text-[11px] font-semibold uppercase tracking-[0.15em] text-brand-cyan hover:text-white inline-flex items-center gap-1 transition-colors">
            {expanded ? "Hide" : "Details"}
            {expanded ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
          </button>
        </div>
      </div>

      {expanded && (
        <ul className="border-t border-border divide-y divide-border/50">
          {data.checks.map((c) => {
            const meta = STATUS_META[c.status] || STATUS_META.info;
            const Icon = meta.icon;
            return (
              <li
                key={c.id}
                data-testid={`security-check-${c.id}`}
                className="px-5 py-3 flex items-start gap-3">
                <span className={`w-6 h-6 rounded flex items-center justify-center shrink-0 ${meta.bg} ${meta.border} border`}>
                  <Icon size={13} weight="bold" className={meta.color} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-slate-100">{c.label}</span>
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.15em] border rounded ${meta.border} ${meta.bg} ${meta.color}`}
                      data-testid={`security-check-${c.id}-status`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                      {meta.label}
                    </span>
                  </div>
                  <div className="text-[12px] text-slate-400 mt-0.5">{c.message}</div>
                  {c.help && (
                    <div className="text-[11px] text-slate-500 mt-1 leading-snug">
                      <span className="text-brand-cyan">Tip:</span> {c.help}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {(fail > 0 || warn > 0) && (
            <li className="px-5 py-3 flex items-center justify-between text-[11px]">
              <span className="text-slate-500">
                Quick actions:
              </span>
              <div className="flex items-center gap-3">
                <Link to="/app/account"
                  data-testid="security-posture-account-link"
                  className="text-brand-cyan hover:text-white underline underline-offset-2">
                  Manage account &amp; sessions
                </Link>
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
